import InfoIcon from "@mui/icons-material/Info";
import { IconButton, Palette, PaletteColor, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import IBuilidng from "../../../models/building";
import { EPeriodicStatus } from "../../../models/periodic/periodic";
import { useTranslatedTaskName } from "../../../utils/taskNameTranslation";
import PeriodicChips from "../../periodics/PeriodicChips";
import { IColumn } from "./DataTable";

interface PeriodicDisplayDate {
    status: EPeriodicStatus;
    scheduleDate: Date;
}
interface StatusInfo {
    key: string;
    color: keyof Palette | "default";
}

interface PeriodicDisplay {
    id: string;
    room: string;
    building: IBuilidng;
    floor: string;
    profile: string;
    taskId?: number;
    taskName: string;
    frequency: string;
    cleaningDate: PeriodicDisplayDate;
}

const dateFormat = "YYYY/MM/DD";

type Color =
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";

interface PeriodicColumnsOptions {
    includeBuilding?: boolean;
    includeRoom?: boolean;
    includeFloor?: boolean;
    includeProfile?: boolean;
    includeTaskName?: boolean;
    includeFrequency?: boolean;
}

const usePeriodicColumns = (options: PeriodicColumnsOptions = {}) => {
    const { t } = useTranslation(["periodical"]);
    const translateTaskName = useTranslatedTaskName();

    const getColor = (displayDate: PeriodicDisplayDate | undefined): Color => {
        if (!displayDate) return "default";

        switch (displayDate.status) {
            case EPeriodicStatus.Accepted:
                return "success";
            case EPeriodicStatus.Refused:
                return "error";
            case EPeriodicStatus.ToVerify:
                return "info";
            default:
                return moment(displayDate.scheduleDate).isBefore(moment(), "day")
                    ? "warning"
                    : "default";
        }
    };

    const statusInfo: StatusInfo[] = [
        {
            key: EPeriodicStatus[EPeriodicStatus.Todo],
            color: "default",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.ToVerify],
            color: "info",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.Accepted],
            color: "success",
        },
        {
            key: EPeriodicStatus[EPeriodicStatus.Refused],
            color: "error",
        },
        { key: "Overdue", color: "warning" },
    ];
    const tooltipContent = statusInfo.map((status) => (
        <>
            <Typography
                variant="subtitle2"
                display="inline"
                sx={{
                    color: (theme) =>
                        status.color === "default"
                            ? theme.palette.grey[500]
                            : (theme.palette[status.color] as PaletteColor)
                                .main,
                }}
            >
                {t(`periodical:status.${status.key}`)}
            </Typography>
            {t(`periodical:statusDescription.${status.key}`)}
            <br />
        </>
    ));

    const baseColumns: IColumn<PeriodicDisplay>[] = [];

    if (options.includeBuilding) {
        baseColumns.push({
            field: "building", headerName: t("common:header.buildings"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {data.building.name}
                    </Typography>
                ),
                filterTransform: (data) => {
                    return data.building.name;
                },
            },
        });
    }
    if (options.includeRoom) {
        baseColumns.push({ field: "room", headerName: t("common:header.rooms") });
    }
    if (options.includeFloor) {
        baseColumns.push({ field: "floor", headerName: t("common:header.floors") });
    }
    if (options.includeProfile) {
        baseColumns.push({ field: "profile", headerName: t("common:header.profiles") });
    }
    if (options.includeTaskName) {
        baseColumns.push({
            field: "taskName",
            headerName: t("common:header.taskNames"),
            transform: {
                render: (data) => (
                    <Typography variant="body2">
                        {translateTaskName({ id: data.taskId, name: data.taskName })}
                    </Typography>
                ),
                filterTransform: (data) => translateTaskName({ id: data.taskId, name: data.taskName }),
            },
        });
    }
    if (options.includeFrequency) {
        baseColumns.push({ field: "frequency", headerName: t("common:header.frequency") });
    }


    baseColumns.push({
        field: "cleaningDate",
        headerName: 'Date',
        renderHeader: (
            <Tooltip
                title={tooltipContent}
                componentsProps={{
                    tooltip: {
                        sx: {
                            color: "black",
                            backgroundColor: "white",
                            fontSize: "18",
                        },
                    },
                }}
            >
                <IconButton>
                    <InfoIcon />
                </IconButton>
            </Tooltip>
        ),
        transform: {
            render: (data) => (
                <PeriodicChips periodic={data.cleaningDate} />
            ),
            filterTransform: (data) => {
                return moment
                    .utc(data.cleaningDate.scheduleDate)
                    .format("DD-MM-YYYY") +
                    " (" + t(`periodical:status.${EPeriodicStatus[data.cleaningDate.status]}`) + ")";
            },
        },
    });

    return baseColumns;
};


export default usePeriodicColumns;